/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useMemo, useEffect, useState } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-table components
import { useTable, usePagination, useGlobalFilter, useAsyncDebounce, useSortBy } from "react-table";

// @mui material components
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Icon from "@mui/material/Icon";
import TablePagination from "@mui/material/TablePagination";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDPagination from "components/MDPagination";

// Material Dashboard 2 React example components
import DataTableHeadCell from "./DataTableHeadCell";
import DataTableBodyCell from "./DataTableBodyCell";
import { Loader } from "components/Custom/Loader";

// Look of the pagination bar: soft divider, pill shaped "rows per page" box,
// round previous / next buttons (maroon, gold on hover)
const paginationSx = ({ palette }) => ({
  borderTop: `1px solid ${palette.light.main}`,
  color: palette.text.main,
  overflow: "hidden",

  "& .MuiTablePagination-toolbar": {
    minHeight: "auto",
    padding: { xs: "12px 8px", sm: "14px 24px" },
    // phones: everything stays in ONE row (arrows right next to the number)
    flexWrap: { xs: "nowrap", sm: "wrap" },
    justifyContent: { xs: "center", sm: "flex-end" },
    gap: 0,
  },

  // on phones the spacer would push everything to the right
  "& .MuiTablePagination-spacer": { display: { xs: "none", sm: "block" } },

  // "Rows per page" label
  "& .MuiTablePagination-selectLabel": {
    margin: "0 6px 0 0",
    fontSize: { xs: "0.8rem", sm: "0.85rem" },
    fontWeight: 700,
    letterSpacing: "0.02em",
    color: palette.primary.main,
    opacity: 1,
    whiteSpace: "nowrap",
  },

  // the 10 / 25 / 50 box
  "& .MuiTablePagination-input": {
    position: "relative", // arrow stays inside this box
    margin: { xs: "0 12px 0 0", sm: "0 20px 0 0" },
    border: `1px solid ${palette.grey[300]}`,
    borderRadius: "50px",
    backgroundColor: palette.white.main,
    transition: "border-color .25s ease, box-shadow .25s ease",
    "&:hover": { borderColor: palette.secondary.main },
    "&.Mui-focused": {
      borderColor: palette.secondary.main,
      boxShadow: `0 0 0 3px ${palette.secondary.main}26`,
    },
  },
  "& .MuiTablePagination-select.MuiSelect-select": {
    boxSizing: "border-box",
    minWidth: "4.2rem", // box wide enough for number + arrow
    paddingLeft: "16px !important",
    paddingRight: "34px !important", // space reserved for the arrow
    paddingTop: "5px !important",
    paddingBottom: "5px !important",
    textAlign: "left",
    textAlignLast: "left",
    fontSize: "0.85rem",
    fontWeight: 700,
    color: palette.primary.main,
    backgroundColor: "transparent !important",
  },

  // dropdown arrow (theme sets it to display:none, so override it)
  "& .MuiTablePagination-selectIcon": {
    display: "block !important",
    position: "absolute",
    right: "8px",
    top: "50%",
    transform: "translateY(-50%)",
    width: "1.25rem",
    height: "1.25rem",
    fontSize: "1.25rem !important",
    lineHeight: 1,
    color: `${palette.primary.main} !important`,
    pointerEvents: "none", // click goes to the select itself
  },

  // "1-10 of 57" / "Page 2 of 5"
  "& .MuiTablePagination-displayedRows": {
    margin: 0,
    fontSize: { xs: "0.8rem", sm: "0.85rem" },
    fontWeight: 600,
    color: palette.primary.main,
    whiteSpace: "nowrap",
  },

  // previous / next: round buttons
  "& .MuiTablePagination-actions": {
    marginLeft: "8px",
    display: "flex",
    gap: "6px",
  },
  "& .MuiTablePagination-actions .MuiIconButton-root": {
    width: { xs: 30, sm: 34 },
    height: { xs: 30, sm: 34 },
    margin: 0,
    padding: 0,
    color: "#000",
    border: `1px solid ${palette.grey[300]}`,
    backgroundColor: palette.grey[200],
    transition: "background-color .25s ease, color .25s ease, border-color .25s ease",
    "& .MuiSvgIcon-root, & .material-icons, & .material-icons-round": { color: "#000" },
    "&:hover": {
      backgroundColor: palette.grey[300],
      borderColor: palette.grey[400],
      color: "#000",
    },
    "&.Mui-disabled": {
      color: palette.grey[500],
      backgroundColor: palette.grey[100],
      borderColor: palette.grey[200],
      opacity: 0.7,
      "& .MuiSvgIcon-root, & .material-icons, & .material-icons-round": {
        color: palette.grey[500],
      },
    },
  },
});

// "Rows per page" on wide screens, just "Rows" on phones so the bar fits in one row
const rowsPerPageLabel = (
  <>
    <MDBox component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
      Rows per page
    </MDBox>
    <MDBox component="span" sx={{ display: { xs: "inline", sm: "none" } }}>
      Rows
    </MDBox>
  </>
);

// Arrow icon: uses the MUI <Icon> (material icons font) already used in this project,
// so no extra package (@mui/icons-material) is needed
// "unfold_more" shows both an up and a down arrow
const SelectArrow = (props) => <Icon {...props}>unfold_more</Icon>;

const paginationSelectProps = {
  IconComponent: SelectArrow,
  MenuProps: {
    PaperProps: {
      sx: ({ palette }) => ({
        marginTop: "6px",
        borderRadius: "12px",
        border: `1px solid ${palette.light.main}`,
        boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
        "& .MuiMenuItem-root": {
          justifyContent: "center",
          fontSize: "0.85rem",
          fontWeight: 600,
          minWidth: "4.5rem",
          "&:hover": { backgroundColor: palette.light.main },
          "&.Mui-selected": {
            backgroundColor: palette.primary.main,
            color: palette.white.main,
            "&:hover": { backgroundColor: palette.primary.main },
          },
        },
      }),
    },
  },
};

function DataTable({
  entriesPerPage,
  canSearch,
  showTotalEntries,
  table,
  pagination,
  isSorted,
  noEndBorder,
  AddButton,
  FindText,
  NextButton,
  PrevButton,
  totalEntries,
  isLoading,
  showPagination,
  serverPagination,
}) {
  const defaultValue = entriesPerPage.defaultValue ? entriesPerPage.defaultValue : 10;
  const entries = entriesPerPage.entries
    ? entriesPerPage.entries.map((el) => el.toString())
    : ["5", "10", "15", "20", "25"];
  const columns = useMemo(() => table.columns, [table]);
  const data = useMemo(() => table.rows, [table]);

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0 },
      // Server side paging: the API already returns ONE page, so react-table must not slice it again
      ...(serverPagination
        ? { manualPagination: true, pageCount: Math.max(serverPagination.totalPages || 0, 1) }
        : {}),
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    pageOptions,
    canPreviousPage,
    canNextPage,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setGlobalFilter,
    state: { pageIndex, pageSize, globalFilter },
  } = tableInstance;

  // Set the default value for the entries per page when component mounts
  useEffect(() => setPageSize(defaultValue || 10), [defaultValue]);

  // Set the entries per page value based on the select value
  const setEntriesPerPage = (value) => setPageSize(value);

  // Render the paginations
  const renderPagination = pageOptions.map((option) => (
    <MDPagination
      item
      key={option}
      onClick={() => gotoPage(Number(option))}
      active={pageIndex === option}
    >
      {option + 1}
    </MDPagination>
  ));

  // Handler for the input to set the pagination index
  const handleInputPagination = ({ target: { value } }) =>
    value > pageOptions.length || value < 0 ? gotoPage(0) : gotoPage(Number(value));

  // Customized page options starting from 1
  const customizedPageOptions = pageOptions.map((option) => option + 1);

  // Setting value for the pagination input
  const handleInputPaginationValue = ({ target: value }) => gotoPage(Number(value.value - 1));

  // Search input value state
  const [search, setSearch] = useState(globalFilter);

  // Search input state handle
  const onSearchChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 100);

  // A function that sets the sorted value for the table
  const setSortedValue = (column) => {
    let sortedValue;

    if (isSorted && column.isSorted) {
      sortedValue = column.isSortedDesc ? "desc" : "asce";
    } else if (isSorted) {
      sortedValue = "none";
    } else {
      sortedValue = false;
    }

    return sortedValue;
  };

  // Setting the entries starting point
  const entriesStart = pageIndex === 0 ? pageIndex + 1 : pageIndex * pageSize + 1;

  let entriesEnd;

  if (pageIndex === 0) {
    entriesEnd = pageSize;
  } else if (pageIndex === pageOptions.length - 1) {
    entriesEnd = rows.length;
  } else {
    entriesEnd = pageSize * (pageIndex + 1);
  }
  const pageSizeOptions = entriesPerPage.entries
    ? entriesPerPage.entries.map(Number)
    : [10, 25, 50];

  let paginationBar = null;

  if (serverPagination) {
    const totalPages = Math.max(serverPagination.totalPages || 0, 1);
    const options = serverPagination.rowsPerPageOptions || pageSizeOptions;

    // If the parent passes the real number of records (totalCount), show "1-10 of 1086"
    // exactly like the client side tables. Without it the API only gives the number of
    // pages, so we fall back to "Page 1 of 109".
    const hasTotalCount = Number.isFinite(serverPagination.totalCount);

    // After changing "rows per page" the old totalPages is still in state until the new API
    // answer arrives, so Next looked enabled for a moment (or stayed enabled on the last page).
    // Fix: lock both arrows while loading, and treat a page with fewer rows than the page size as the last one.
    const isLastPage =
      serverPagination.page + 1 >= totalPages ||
      (Number.isFinite(serverPagination.currentCount) &&
        serverPagination.currentCount < serverPagination.rowsPerPage);
    const nextDisabled = Boolean(isLoading) || isLastPage;
    const prevDisabled = Boolean(isLoading) || serverPagination.page <= 0;

    paginationBar = (
      <TablePagination
        component="div"
        sx={paginationSx}
        count={
          hasTotalCount ? serverPagination.totalCount : totalPages * serverPagination.rowsPerPage
        }
        page={serverPagination.page}
        rowsPerPage={serverPagination.rowsPerPage}
        rowsPerPageOptions={options}
        onPageChange={(e, newPage) => serverPagination.onPageChange(newPage)}
        onRowsPerPageChange={(e) => serverPagination.onRowsPerPageChange(Number(e.target.value))}
        labelRowsPerPage={rowsPerPageLabel}
        SelectProps={paginationSelectProps}
        nextIconButtonProps={{ disabled: nextDisabled }}
        backIconButtonProps={{ disabled: prevDisabled }}
        {...(hasTotalCount
          ? {}
          : {
              // total records unknown: show only the range on this page, e.g. "11-20"
              labelDisplayedRows: () => {
                const from = serverPagination.page * serverPagination.rowsPerPage + 1;
                const shown = Number.isFinite(serverPagination.currentCount)
                  ? serverPagination.currentCount
                  : serverPagination.rowsPerPage;
                return shown > 0 ? `${from}–${from + shown - 1}` : "0";
              },
            })}
      />
    );
  } else if (showPagination && rows.length > 0) {
    paginationBar = (
      <TablePagination
        component="div"
        sx={paginationSx}
        count={rows.length}
        page={pageIndex}
        rowsPerPage={pageSize}
        rowsPerPageOptions={pageSizeOptions}
        onPageChange={(e, newPage) => gotoPage(newPage)}
        onRowsPerPageChange={(e) => setEntriesPerPage(Number(e.target.value))}
        labelRowsPerPage={rowsPerPageLabel}
        SelectProps={paginationSelectProps}
      />
    );
  }

  return (
    <TableContainer sx={{ boxShadow: "none" }}>
      {(entriesPerPage || canSearch) && (FindText || AddButton) ? (
        <MDBox
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          gap={{ xs: 1.5, sm: 2 }}
          p={{ xs: 2, sm: 3 }}
        >
          {/* only render the search slot when a search box is passed (e.g. Blog has none) */}
          {FindText && (
            // Search stays on the left when an Add button shares the row, otherwise it sits on the right
            <MDBox
              width={{ xs: "100%", sm: "12rem" }}
              mr={{ sm: AddButton ? "auto" : 0 }}
              ml={{ sm: AddButton ? 0 : "auto" }}
            >
              {FindText}
            </MDBox>
          )}

          {AddButton && (
            <MDBox width={{ xs: "100%", sm: "12rem" }} ml={{ sm: "auto" }}>
              {AddButton}
            </MDBox>
          )}
        </MDBox>
      ) : null}
      {isLoading ? (
        <Loader />
      ) : (
        <Table {...getTableProps()}>
          <MDBox component="thead">
            {headerGroups.map((headerGroup, key) => (
              <TableRow key={key} {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column, idx) => (
                  <DataTableHeadCell
                    key={idx}
                    {...column.getHeaderProps(isSorted && column.getSortByToggleProps())}
                    width={column.width ? column.width : "auto"}
                    align={column.align ? column.align : "left"}
                    sorted={setSortedValue(column)}
                  >
                    {column.render("Header")}
                  </DataTableHeadCell>
                ))}
              </TableRow>
            ))}
          </MDBox>
          <TableBody {...getTableBodyProps()}>
            {page.map((row, key) => {
              prepareRow(row);
              return (
                <TableRow key={key} {...row.getRowProps()}>
                  {row.cells.map((cell, idx) => (
                    <DataTableBodyCell
                      key={idx}
                      label={typeof cell.column.Header === "string" ? cell.column.Header : ""}
                      noBorder={noEndBorder && rows.length - 1 === key}
                      align={cell.column.align ? cell.column.align : "left"}
                      {...cell.getCellProps()}
                    >
                      {cell.render("Cell")}
                    </DataTableBodyCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {paginationBar}

      {/* Old pagination (kept for the small tables that do not opt in) */}
      {!showPagination && !serverPagination && (
        <MDBox
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          justifyContent="center"
          alignItems="center"
          p={!showTotalEntries && pageOptions.length === 1 ? 0 : 3}
        >
          {/* {showTotalEntries && (
          <MDBox mb={{ xs: 3, sm: 0 }}>
            <MDTypography variant="button" color="secondary" fontWeight="regular">
              Showing {entriesStart} to {entriesEnd} of {rows.length} entries
            </MDTypography>
          </MDBox>
        )} */}
          {/* {pageOptions.length > 1 && ( */}
          <MDPagination
            variant={pagination.variant ? pagination.variant : "gradient"}
            color={pagination.color ? pagination.color : "info"}
          >
            {canPreviousPage && (
              <MDPagination item onClick={() => previousPage()}>
                <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
              </MDPagination>
            )}
            {/* {renderPagination.length > 6 ? ( */}
            {/* <MDBox width="5rem" mx={1}>
            <MDInput
              inputProps={{ type: "number", min: 1, max: customizedPageOptions.length }}
              value={customizedPageOptions[pageIndex]}
              onChange={(handleInputPagination, handleInputPaginationValue)}
            />
          </MDBox> */}
            {/* ) : (
              renderPagination
            )} */}

            {PrevButton}
            {totalEntries}
            {NextButton}
            {/* {canNextPage && (
            <MDPagination item onClick={() => nextPage()}>
              <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
            </MDPagination>
          )} */}
          </MDPagination>
          {/* )} */}
        </MDBox>
      )}
    </TableContainer>
  );
}
DataTable.defaultProps = {
  entriesPerPage: { defaultValue: 10, entries: [5, 10, 15, 20, 25] },
  canSearch: false,
  showTotalEntries: true,
  pagination: { variant: "gradient", color: "info" },
  isSorted: true,
  noEndBorder: false,
  showPagination: false,
  serverPagination: null,
};
DataTable.propTypes = {
  entriesPerPage: PropTypes.oneOfType([
    PropTypes.shape({
      defaultValue: PropTypes.number,
      entries: PropTypes.arrayOf(PropTypes.number),
    }),
    PropTypes.bool,
  ]),
  canSearch: PropTypes.bool,
  showTotalEntries: PropTypes.bool,
  table: PropTypes.objectOf(PropTypes.array).isRequired,
  pagination: PropTypes.shape({
    variant: PropTypes.oneOf(["contained", "gradient"]),
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "light",
    ]),
  }),
  isSorted: PropTypes.bool,
  noEndBorder: PropTypes.bool,
  AddButton: PropTypes.node,
  FindText: PropTypes.node,
  NextButton: PropTypes.node,
  PrevButton: PropTypes.node,
  totalEntries: PropTypes.node,
  isLoading: PropTypes.node,
  showPagination: PropTypes.bool,
  // paging done by the API (parent page owns page + rowsPerPage). page is 0 based.
  serverPagination: PropTypes.shape({
    page: PropTypes.number.isRequired,
    rowsPerPage: PropTypes.number.isRequired,
    totalPages: PropTypes.number,
    totalCount: PropTypes.number, // total records, shows "1-10 of N"
    currentCount: PropTypes.number, // rows on the current page, used for the "11-20" range
    rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
    onPageChange: PropTypes.func.isRequired,
    onRowsPerPageChange: PropTypes.func.isRequired,
  }),
};

export default DataTable;
